import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useState } from 'react';
import {
  Code,
  BookOpen,
  Download,
  FileText,
  Shield,
  HelpCircle,
  ExternalLink,
  FileDown,
  GamepadIcon,
  ChevronDown,
  Rocket,
  LayoutDashboard,
  CircleUser,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SdkComingSoonBanner, SdkComingSoonBadge } from '@/components/developer/SdkComingSoonNotice';
import { cn } from '@/lib/utils';

interface DeveloperSidebarProps {
  profile: { id: string; displayName: string } | null;
}

type DocItem = {
  href: string;
  label: string;
  icon: typeof Code;
  description: string;
};

const DOC_ITEMS: DocItem[] = [
  {
    href: '/developer',
    label: 'Overview',
    icon: LayoutDashboard,
    description: 'Hub, analytics preview and shortcuts',
  },
  {
    href: '/developer/quick-start',
    label: 'Quick Start',
    icon: FileDown,
    description: 'Copy-paste ready example',
  },
  {
    href: '/developer/getting-started',
    label: 'Getting Started',
    icon: BookOpen,
    description: 'Step-by-step guide',
  },
  {
    href: '/developer/installation',
    label: 'Installation',
    icon: Download,
    description: 'NPM and CDN setup',
  },
  {
    href: '/developer/player-session',
    label: 'Player session',
    icon: CircleUser,
    description: 'Identity, saves, and session changes',
  },
  {
    href: '/developer/payments',
    label: 'Payments',
    icon: Shield,
    description: 'Payment processing',
  },
  {
    href: '/developer/api-reference',
    label: 'API Reference',
    icon: FileText,
    description: 'Complete API docs',
  },
  {
    href: '/developer/examples',
    label: 'Examples',
    icon: Code,
    description: 'Code examples',
  },
  {
    href: '/developer/error-handling',
    label: 'Error Handling',
    icon: HelpCircle,
    description: 'Error types and patterns',
  },
  {
    href: '/developer/security',
    label: 'Security',
    icon: Shield,
    description: 'Security best practices',
  },
  {
    href: '/developer/faq',
    label: 'FAQ',
    icon: HelpCircle,
    description: 'Common questions',
  },
];

/** On `/developer`, skip repeating the full TOC; surface studio tasks + expandable guides. */
const HUB_PREVIEW_DOC_HREFS = new Set([
  '/developer/quick-start',
  '/developer/getting-started',
  '/developer/player-session',
  '/developer/api-reference',
]);

const externalLinks = [
  {
    href: 'https://www.npmjs.com/package/@arcadiasol/game-sdk',
    label: 'NPM Package',
    icon: ExternalLink,
    description: 'Packages ship with the SDK release',
  },
];

export function DeveloperSidebar({ profile }: DeveloperSidebarProps) {
  const { pathname } = useLocation();
  const isDeveloperHub = pathname === '/developer';
  const [hubGuidesOpen, setHubGuidesOpen] = useState(false);

  const isActive = (href: string) => {
    if (href === '/developer') {
      return pathname === '/developer';
    }
    return pathname?.startsWith(href) ?? false;
  };

  const docNavItems = isDeveloperHub
    ? DOC_ITEMS.filter((item) => item.href !== '/developer' && HUB_PREVIEW_DOC_HREFS.has(item.href))
    : DOC_ITEMS.filter((item) => item.href !== '/developer');

  const hubOverflowDocs = DOC_ITEMS.filter(
    (item) => item.href !== '/developer' && !HUB_PREVIEW_DOC_HREFS.has(item.href),
  );

  const renderDocLink = (item: DocItem, compact?: boolean) => {
    const Icon = item.icon;
    const active = isActive(item.href);
    return (
      <Link key={item.href} to={item.href}>
        <Button
          variant={active ? 'default' : 'ghost'}
          className={cn(
            'h-auto w-full justify-start px-3 py-2',
            active
              ? 'rounded-r-lg border-l-2 border-nav-active bg-blue-500/10 text-nav-active hover:bg-blue-500/15'
              : 'border-l-2 border-transparent text-arc-text-main hover:bg-white/5',
            compact && 'py-1.5',
          )}
        >
          <Icon className="mr-2 h-4 w-4 flex-shrink-0" />
          <div className="min-w-0 flex-1 text-left">
            <div className="text-sm font-medium">{item.label}</div>
            {!compact && <div className="text-xs text-arc-text-muted">{item.description}</div>}
          </div>
        </Button>
      </Link>
    );
  };

  return (
    <div className="arc-aside-surface fixed left-0 top-16 z-30 hidden h-[calc(100vh-4rem)] w-64 flex-col overflow-y-auto md:flex">
      <div className="border-b border-white/10 p-4">
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <Code className="h-5 w-5 shrink-0 text-arc-primary" />
            <h2 className="text-lg font-semibold text-arc-text-main">
              {isDeveloperHub ? 'Developer' : 'Developer docs'}
            </h2>
          </div>
          <SdkComingSoonBadge className="shrink-0" />
        </div>
        <SdkComingSoonBanner compact className="mt-2" />
      </div>

      <div className="border-b border-white/10 p-4">
        <p className="mb-2 font-display text-[10px] font-semibold uppercase tracking-wider text-arc-text-meta">
          Studio
        </p>
        <Link to="/developer">
          <Button
            variant={pathname === '/developer' ? 'default' : 'ghost'}
            className={cn(
              'mb-1 h-auto w-full justify-start px-3 py-2',
              pathname === '/developer'
                ? 'rounded-r-lg border-l-2 border-nav-active bg-blue-500/10 text-nav-active hover:bg-blue-500/15'
                : 'border-l-2 border-transparent text-arc-text-main hover:bg-white/5',
            )}
          >
            <LayoutDashboard className="mr-2 h-4 w-4 flex-shrink-0" />
            <div className="flex-1 text-left">
              <div className="text-sm font-medium">Hub overview</div>
              <div className="text-xs text-arc-text-muted">Dashboard preview and shortcuts</div>
            </div>
          </Button>
        </Link>
        <Link to="/developer/portal">
          <Button
            variant={pathname?.startsWith('/developer/portal') ? 'default' : 'ghost'}
            className={cn(
              'mb-1 h-auto w-full justify-start px-3 py-2',
              pathname?.startsWith('/developer/portal')
                ? 'rounded-r-lg border-l-2 border-nav-active bg-blue-500/10 text-nav-active hover:bg-blue-500/15'
                : 'border-l-2 border-transparent text-arc-text-main hover:bg-white/5',
            )}
          >
            <GamepadIcon className="mr-2 h-4 w-4 flex-shrink-0" />
            <div className="flex-1 text-left">
              <div className="text-sm font-medium">Developer portal</div>
              <div className="text-xs text-arc-text-muted">Games, team and listings</div>
            </div>
          </Button>
        </Link>
        <Link to="/store/submit">
          <Button
            variant="ghost"
            className="h-auto w-full justify-start border-l-2 border-transparent px-3 py-2 text-arc-text-main hover:bg-white/5"
          >
            <Rocket className="mr-2 h-4 w-4 flex-shrink-0 text-[var(--arc-cyan-primary)]" />
            <div className="flex-1 text-left">
              <div className="text-sm font-medium">Submit a game</div>
              <div className="text-xs text-arc-text-muted">New store listing</div>
            </div>
          </Button>
        </Link>
      </div>

      {!isDeveloperHub && (
        <div className="border-b border-white/10 p-4">
          <Link to="/developer/quick-start">
            <Button
              className="w-full bg-gradient-to-r from-blue-600 to-blue-400 font-display text-sm font-bold text-white shadow-lg shadow-blue-500/20 hover:opacity-95 active:scale-[0.98]"
              size="sm"
            >
              <FileDown className="mr-2 h-4 w-4" />
              Quick start (preview)
            </Button>
          </Link>
          <p className="mt-2 text-center text-[10px] text-amber-200/90">SDK coming soon</p>
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <div className="border-b border-white/10 p-4">
          <h3 className="mb-3 text-sm font-medium text-arc-text-main">
            {isDeveloperHub ? 'SDK guides (preview)' : 'Documentation (preview)'}
          </h3>
          <div className="space-y-1">
            {isDeveloperHub ? (
              <>
                {docNavItems.map((item) => renderDocLink(item))}
                <button
                  type="button"
                  onClick={() => setHubGuidesOpen((o) => !o)}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-[var(--arc-cyan-primary)] transition-colors hover:bg-white/5"
                >
                  <ChevronDown
                    className={cn('h-4 w-4 transition-transform', hubGuidesOpen && 'rotate-180')}
                  />
                  {hubGuidesOpen ? 'Hide additional guides' : `All guides (${hubOverflowDocs.length} more)`}
                </button>
                {hubGuidesOpen && (
                  <div className="mt-1 space-y-0.5 border-l border-white/10 pl-2">
                    {hubOverflowDocs.map((item) => renderDocLink(item, true))}
                  </div>
                )}
              </>
            ) : (
              docNavItems.map((item) => renderDocLink(item))
            )}
          </div>
        </div>

        <div className="border-b border-arc-border p-4">
          <h3 className="mb-3 text-sm font-medium text-arc-text-main">Resources</h3>
          <div className="space-y-1">
            {externalLinks.map((link) => {
              const Icon = link.icon;
              return (
                <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer">
                  <Button
                    variant="ghost"
                    className="h-auto w-full justify-start px-3 py-2 text-arc-text-main hover:bg-white/5"
                  >
                    <Icon className="mr-2 h-4 w-4 flex-shrink-0" />
                    <div className="flex-1 text-left">
                      <div className="text-sm font-medium">{link.label}</div>
                      <div className="text-xs text-arc-text-muted">{link.description}</div>
                    </div>
                  </Button>
                </a>
              );
            })}
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 bg-black/20 p-4">
        <div className="space-y-1 text-xs text-arc-text-muted">
          <div className="flex items-center gap-2">
            <Code className="h-3 w-3" />
            <span>SDK · coming soon</span>
          </div>
          {profile && <div>Developer: {profile.displayName}</div>}
        </div>
      </div>
    </div>
  );
}
